import * as React from 'react';
import { Form, ProgressBar } from 'react-bootstrap';
import { getLocalization } from 'global/global';
import { ModalComponentNames, ModalInterface } from 'Interfaces/ModalInterface';
import { InviteTemplate } from 'Interfaces/Admin';
import { fetchInviteTemplate, saveInviteTemplate } from 'api/adminApi';
import GenericModal from 'views/Modals/GenericModal';
import { useAppDispatch } from 'index';
import { showAlert } from 'actions';

interface Props {
  /** Google sign-in users get their own template. */
  usesSso: boolean;
  onClose: () => void;
}

/** Placeholders the template must contain to be usable. */
const REQUIRED_PLACEHOLDERS = ['%username%', '%password%', '%account_name%', '%link%'];

/** Replaces TemplateView.js. */
export default function InviteTemplateModal(props: Props) {
  const dispatch = useAppDispatch();
  const [template, setTemplate] = React.useState<InviteTemplate | null>(null);
  const [body, setBody] = React.useState('');
  const [subject, setSubject] = React.useState('');

  React.useEffect(() => {
    fetchInviteTemplate().then((loaded) => {
      setTemplate(loaded);
      const initial = props.usesSso && loaded.email_template_google
        ? loaded.email_template_google
        : loaded.email_template || loaded.default_email_template || '';
      setBody(initial);
      setSubject(loaded.email_template_subject || '');
    }).catch((e) => console.log(e));
  }, [props.usesSso]);

  const onSave = async () => {
    const missing = REQUIRED_PLACEHOLDERS.filter((p) => body.indexOf(p) === -1);
    if (missing.length > 0) {
      dispatch(showAlert({
        visible: true,
        message: missing
          .map((p) => getLocalization('mandatoryTemplateText').replace('%x%', p))
          .join('\n'),
      }));
      return;
    }
    const response = await saveInviteTemplate([
      { name: props.usesSso ? 'email_template_google' : 'email_template', value: body },
      { name: 'email_template_subject', value: subject },
    ]);
    if (response.status === 'OK') {
      props.onClose();
    } else {
      dispatch(showAlert({ visible: true, message: getLocalization('errorUpdatingTemplates') }));
    }
  };

  return (
    <GenericModal
      visible
      title={getLocalization('editTemplate')}
      cancel={props.onClose}
      cancelText={getLocalization('cancel')}
      confirmText={getLocalization('saveTemplate')}
      onConfirm={() => void onSave()}
      body={(
        <>
          {!template && <ProgressBar striped now={100} animated />}
          <Form.Group>
            <Form.Label>{getLocalization('emailTemplate')}</Form.Label>
            <Form.Control
              size="sm"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </Form.Group>
          <p className="text-info">{getLocalization('templateInfo')}</p>
          <Form.Group>
            {/*
              The legacy form used a wysihtml5 editor here. The body is HTML with
              placeholders that are validated by substring match, so it is edited
              as source until the app gains a rich text component.
            */}
            <Form.Control
              as="textarea"
              rows={10}
              value={body}
              placeholder={getLocalization('editTemplatePlaceholder')}
              onChange={(e) => setBody(e.target.value)}
            />
          </Form.Group>
          <p className="text-info"><i>{getLocalization('templateAlert')}</i></p>
        </>
      )}
    />
  );
}

export const showInviteTemplate = (usesSso: boolean) => {
  const modalProps: ModalInterface<Props> = {
    component: ModalComponentNames.InviteTemplateModal,
    props: {
      usesSso,
      onClose: () => {
        (window as any).ReactAppBridge.ReduxActions
          .navigateRemoveModal(ModalComponentNames.InviteTemplateModal);
      },
    },
  };
  (window as any).ReactAppBridge.ReduxActions.navigateAddModal(modalProps);
};
